import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { lotesAPI, expensesAPI, reportsAPI, authAPI, ventasAPI, farmRecordsAPI } from '../lib/api';
import { formatCurrency, formatNumber, formatDate, CATEGORIAS, FASES, getPhaseLabel, getCategoryColor } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Separator } from '../components/ui/separator';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import {
  Bird,
  CalendarBlank,
  CurrencyDollar,
  Plus,
  FilePdf,
  CheckCircle,
  Trash,
  ArrowLeft,
  TrendUp,
  TrendDown,
  Warning,
  DownloadSimple,
  Clipboard,
  ShieldCheck,
  PencilSimple,
  Funnel,
} from '@phosphor-icons/react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts';

export default function LoteDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [lote, setLote] = useState(null);
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCloseDialog, setShowCloseDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [closeData, setCloseData] = useState({
    ingreso_venta: '',
    cantidad_vendida: '',
    mortalidad: '0',
  });
  const [actionLoading, setActionLoading] = useState(false);
  const [deleteSuperuserCode, setDeleteSuperuserCode] = useState('');
  const [closeSuperuserCode, setCloseSuperuserCode] = useState('');
  const [loadingCloseData, setLoadingCloseData] = useState(false);

  // Modal para eliminar/editar gastos con super usuario
  const [showExpenseDeleteModal, setShowExpenseDeleteModal] = useState(false);
  const [expenseToDelete, setExpenseToDelete] = useState(null);
  const [expenseDeleteCode, setExpenseDeleteCode] = useState('');
  const [deletingExpense, setDeletingExpense] = useState(false);
  
  const [showExpenseEditModal, setShowExpenseEditModal] = useState(false);
  const [expenseToEdit, setExpenseToEdit] = useState(null);
  const [expenseEditCode, setExpenseEditCode] = useState('');
  const [validatingExpenseEdit, setValidatingExpenseEdit] = useState(false);

  // Filtros de gastos
  const [expenseFilters, setExpenseFilters] = useState({
    fase: '',
    categoria: '',
  });

  useEffect(() => {
    loadData();
  }, [id]);

  const loadData = async () => {
    try {
      const [loteRes, expensesRes] = await Promise.all([
        lotesAPI.getOne(id),
        expensesAPI.getAll({ lote_id: id }),
      ]);
      setLote(loteRes.data);
      setExpenses(expensesRes.data);
    } catch (error) {
      toast.error('Error al cargar el lote');
      navigate('/lotes');
    } finally {
      setLoading(false);
    }
  };

  // Cargar datos para cierre de lote (ventas y mortalidad)
  const loadCloseData = async () => {
    setLoadingCloseData(true);
    try {
      // Cargar ventas del lote
      const ventasRes = await ventasAPI.getByLote(id);
      const ventas = ventasRes.data || [];
      
      // Calcular totales de ventas
      const totalVentas = ventas.reduce((sum, v) => sum + (v.total_factura || 0), 0);
      const totalPollosVendidos = ventas.reduce((sum, v) => sum + (v.cantidad_pollos || 0), 0);
      
      // Cargar record de granja para mortalidad
      let totalMortalidad = 0;
      let totalDescarte = 0;
      try {
        const recordRes = await farmRecordsAPI.get(id);
        const record = recordRes.data;
        if (record && record.weekly_records && record.weekly_records.length > 0) {
          // Obtener la última semana para tener los acumulados totales
          const ultimaSemana = record.weekly_records[record.weekly_records.length - 1];
          totalMortalidad = ultimaSemana.mortalidad_acumulada || 0;
          totalDescarte = ultimaSemana.descarte_acumulado || 0;
        }
      } catch (e) {
        // Si no hay record de granja, mortalidad es 0
        // Debug removed
      }
      
      setCloseData({
        ingreso_venta: totalVentas > 0 ? totalVentas.toString() : '',
        cantidad_vendida: totalPollosVendidos > 0 ? totalPollosVendidos.toString() : '',
        mortalidad: (totalMortalidad + totalDescarte).toString(),
      });
    } catch (error) {
      // Error silenciado
      toast.error('Error al cargar datos de ventas');
    } finally {
      setLoadingCloseData(false);
    }
  };

  // Abrir modal de cierre y cargar datos
  const openCloseDialog = () => {
    setShowCloseDialog(true);
    loadCloseData();
  };

  const handleCloseLote = async () => {
    if (!closeData.ingreso_venta) {
      toast.error('Ingrese el monto de venta');
      return;
    }
    if (!closeSuperuserCode) {
      toast.error('Ingrese el código de super usuario');
      return;
    }

    setActionLoading(true);
    try {
      // Validar código primero
      await authAPI.validateSuperuser(closeSuperuserCode);
      
      // Cerrar lote
      await lotesAPI.close(id, {
        ingreso_venta: parseFloat(closeData.ingreso_venta),
        cantidad_vendida: closeData.cantidad_vendida ? parseInt(closeData.cantidad_vendida) : null,
        mortalidad: parseInt(closeData.mortalidad) || 0,
      }, closeSuperuserCode);
      toast.success('Lote cerrado exitosamente');
      setShowCloseDialog(false);
      setCloseSuperuserCode('');
      loadData();
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error('Código de super usuario inválido');
      } else {
        toast.error(error.response?.data?.detail || 'Error al cerrar el lote');
      }
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteLote = async () => {
    if (!deleteSuperuserCode) {
      toast.error('Ingrese el código de super usuario');
      return;
    }
    
    setActionLoading(true);
    try {
      await authAPI.validateSuperuser(deleteSuperuserCode);
      await lotesAPI.delete(id, deleteSuperuserCode);
      toast.success('Lote eliminado');
      navigate('/lotes');
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error('Código de super usuario inválido');
      } else {
        toast.error('Error al eliminar el lote');
      }
    } finally {
      setActionLoading(false);
    }
  };

  // Funciones para eliminar/editar gastos con super usuario
  const openExpenseDeleteModal = (expense) => {
    setExpenseToDelete(expense);
    setExpenseDeleteCode('');
    setShowExpenseDeleteModal(true);
  };

  const openExpenseEditModal = (expense) => {
    setExpenseToEdit(expense);
    setExpenseEditCode('');
    setShowExpenseEditModal(true);
  };

  const handleDeleteExpense = async () => {
    if (!expenseDeleteCode || !expenseToDelete) {
      toast.error('Ingrese el código de super usuario');
      return;
    }
    
    setDeletingExpense(true);
    try {
      await authAPI.validateSuperuser(expenseDeleteCode);
      await expensesAPI.delete(expenseToDelete.id, expenseDeleteCode);
      toast.success('Gasto eliminado');
      setShowExpenseDeleteModal(false);
      loadData();
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error('Código de super usuario inválido');
      } else {
        toast.error('Error al eliminar el gasto');
      }
    } finally {
      setDeletingExpense(false);
    }
  };

  const handleValidateExpenseEdit = async () => {
    if (!expenseEditCode) {
      toast.error('Ingrese el código de super usuario');
      return;
    }
    setValidatingExpenseEdit(true);
    try {
      await authAPI.validateSuperuser(expenseEditCode);
      setShowExpenseEditModal(false);
      // Navegar al formulario de edición
      navigate(`/gastos/editar/${expenseToEdit.id}?code=${encodeURIComponent(expenseEditCode)}`);
    } catch (error) {
      toast.error('Código de super usuario inválido');
    } finally {
      setValidatingExpenseEdit(false);
    }
  };

  const handleDownloadPDF = async (type, categoria = null) => {
    try {
      toast.loading('Generando PDF...');
      if (type === 'estado-resultados') {
        await reportsAPI.downloadEstadoResultados(id, lote.nombre);
      } else if (type === 'categoria' && categoria) {
        await reportsAPI.downloadCategoriaReport(id, categoria, lote.nombre);
      } else if (type === 'record-granja') {
        await reportsAPI.downloadRecordGranja(id, lote.nombre);
      } else if (type === 'control-alimento') {
        await reportsAPI.downloadControlAlimento(id, lote.nombre);
      } else if (type === 'ventas') {
        await reportsAPI.downloadVentas(id, lote.nombre);
      }
      toast.dismiss();
      toast.success('PDF descargado');
    } catch (error) {
      toast.dismiss();
      toast.error('Error al generar el PDF');
    }
  };

  // Filtrar gastos según los filtros seleccionados
  const filteredExpenses = expenses.filter((exp) => {
    if (expenseFilters.fase && exp.fase !== expenseFilters.fase) return false;
    if (expenseFilters.categoria && exp.categoria !== expenseFilters.categoria) return false;
    return true;
  });

  // Prepare chart data
  const expensesByCategory = expenses.reduce((acc, exp) => {
    acc[exp.categoria] = (acc[exp.categoria] || 0) + exp.total;
    return acc;
  }, {});

  const pieData = Object.entries(expensesByCategory).map(([key, value]) => ({
    name: CATEGORIAS.find((c) => c.value === key)?.label || key,
    value,
    color: getCategoryColor(key),
  }));

  const expensesByPhase = expenses.reduce((acc, exp) => {
    acc[exp.fase] = (acc[exp.fase] || 0) + exp.total;
    return acc;
  }, {});

  const barData = Object.entries(expensesByPhase).map(([fase, total]) => ({
    name: `Fase ${fase}`,
    total,
  }));

  if (loading) {
    return (
      <Layout>
        <div className="space-y-6">
          <Skeleton className="h-10 w-64" />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-32" />
            ))}
          </div>
          <Skeleton className="h-64" />
        </div>
      </Layout>
    );
  }

  if (!lote) {
    return (
      <Layout>
        <div className="text-center py-12">
          <p>Lote no encontrado</p>
          <Link to="/lotes">
            <Button className="mt-4">Volver a Lotes</Button>
          </Link>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6 animate-fade-in" data-testid="lote-detail">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <Link
              to="/lotes"
              className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1 mb-2"
            >
              <ArrowLeft size={14} />
              Volver a Lotes
            </Link>
            <div className="flex items-center gap-3">
              <h1 className="text-3xl font-bold tracking-tight uppercase" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                {lote.nombre}
              </h1>
              <Badge
                variant="outline"
                className={lote.estado === 'activo' ? 'status-activo' : 'status-cerrado'}
              >
                {lote.estado === 'activo' ? 'Activo' : 'Cerrado'}
              </Badge>
              {lote.estado === 'activo' && (
                <Badge
                  variant="outline"
                  className={
                    lote.dias_transcurridos >= 60
                      ? 'alert-critical'
                      : lote.dias_transcurridos >= 45
                      ? 'alert-warning'
                      : ''
                  }
                >
                  Día {lote.dias_transcurridos}
                </Badge>
              )}
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              onClick={() => handleDownloadPDF('estado-resultados')}
              data-testid="download-pdf-btn"
            >
              <FilePdf size={18} className="mr-2" />
              Estado de Resultados
            </Button>
            <Link to={`/lotes/${id}/record`}>
              <Button variant="outline" data-testid="farm-record-btn">
                <Clipboard size={18} className="mr-2" />
                Record de Granja
              </Button>
            </Link>
            <Link to={`/lotes/${id}/comparacion-alimento`}>
              <Button variant="outline" data-testid="comparacion-alimento-btn">
                <TrendUp size={18} className="mr-2" />
                Comparar Alimento
              </Button>
            </Link>
            {lote.estado === 'activo' && (
              <>
                <Link to={`/gastos/nuevo?lote_id=${id}`}>
                  <Button className="bg-[#F69031] hover:bg-[#E08020] text-white" data-testid="add-expense-btn">
                    <Plus size={18} className="mr-2" />
                    Agregar Gasto
                  </Button>
                </Link>
                <Link to={`/lotes/${id}/editar`}>
                  <Button variant="outline" className="border-blue-500 text-blue-600 hover:bg-blue-50" data-testid="edit-lote-btn">
                    <PencilSimple size={18} className="mr-2" />
                    Editar Lote
                  </Button>
                </Link>
                <Button
                  className="bg-[#E73425] hover:bg-[#D81C23]"
                  onClick={openCloseDialog}
                  data-testid="close-lote-btn"
                >
                  <CheckCircle size={18} className="mr-2" />
                  Cerrar Lote
                </Button>
              </>
            )}
          </div>
        </div>

        {/* Alerts */}
        {lote.alertas && lote.alertas.length > 0 && (
          <div className="space-y-2">
            {lote.alertas.map((alerta, idx) => (
              <div
                key={idx}
                className={`flex items-center gap-3 p-4 rounded-lg border ${
                  lote.dias_transcurridos >= 60
                    ? 'bg-red-50 border-red-200'
                    : 'bg-orange-50 border-orange-200'
                }`}
              >
                <Warning
                  size={24}
                  weight="fill"
                  className={lote.dias_transcurridos >= 60 ? 'text-red-500' : 'text-orange-500'}
                />
                <p className="font-medium">{alerta}</p>
              </div>
            ))}
          </div>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="metric-card">
            <CardContent className="pt-5">
              <p className="metric-label">Pollitos</p>
              <p className="metric-value text-[#E73425]">{formatNumber(lote.cantidad_pollitos)}</p>
              <p className="text-sm text-muted-foreground mt-1">
                ${lote.precio_pollito}/unidad
              </p>
            </CardContent>
          </Card>

          <Card className="metric-card">
            <CardContent className="pt-5">
              <p className="metric-label">Total Inversión</p>
              <p className="metric-value text-[#F69031]">RD$ {lote.total_inversion?.toLocaleString('en-US', { minimumFractionDigits: 2 }) || '0.00'}</p>
              <p className="text-sm text-green-600 font-semibold">
                US$ {expenses.reduce((sum, e) => sum + (e.total_usd || 0), 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
              </p>
              {lote.costo_por_pollo && (
                <p className="text-xs text-muted-foreground mt-1">
                  {formatCurrency(lote.costo_por_pollo)}/pollo
                </p>
              )}
            </CardContent>
          </Card>

          {lote.estado === 'cerrado' && (
            <>
              <Card className="metric-card">
                <CardContent className="pt-5">
                  <p className="metric-label">Ingreso Venta</p>
                  <p className="metric-value text-emerald-600">{formatCurrency(lote.ingreso_venta)}</p>
                  {lote.cantidad_vendida && (
                    <p className="text-sm text-muted-foreground mt-1">
                      {formatNumber(lote.cantidad_vendida)} vendidos
                    </p>
                  )}
                </CardContent>
              </Card>

              <Card className="metric-card">
                <CardContent className="pt-5">
                  <p className="metric-label">
                    {lote.ganancia_neta >= 0 ? 'Ganancia Neta' : 'Pérdida Neta'}
                  </p>
                  <p
                    className={`metric-value flex items-center gap-2 ${
                      lote.ganancia_neta >= 0 ? 'text-emerald-600' : 'text-red-500'
                    }`}
                  >
                    {lote.ganancia_neta >= 0 ? (
                      <TrendUp size={24} weight="bold" />
                    ) : (
                      <TrendDown size={24} weight="bold" />
                    )}
                    {formatCurrency(Math.abs(lote.ganancia_neta))}
                  </p>
                </CardContent>
              </Card>
            </>
          )}

          {lote.estado === 'activo' && (
            <>
              <Card className="metric-card">
                <CardContent className="pt-5">
                  <p className="metric-label">Días Transcurridos</p>
                  <p className="metric-value text-slate-600">{lote.dias_transcurridos}</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    de 45 días objetivo
                  </p>
                </CardContent>
              </Card>

              <Card className="metric-card">
                <CardContent className="pt-5">
                  <p className="metric-label">Fecha de Venta</p>
                  <p className="text-lg font-semibold text-slate-600">
                    {formatDate(
                      new Date(
                        new Date(lote.fecha_inicio).getTime() + 45 * 24 * 60 * 60 * 1000
                      ).toISOString()
                    )}
                  </p>
                  <p className="text-sm text-muted-foreground mt-1">
                    estimada (día 45)
                  </p>
                </CardContent>
              </Card>
            </>
          )}
        </div>

        {/* Charts & Expenses */}
        <Tabs defaultValue="gastos" className="space-y-4">
          <TabsList>
            <TabsTrigger value="gastos">Gastos</TabsTrigger>
            <TabsTrigger value="graficos">Gráficos</TabsTrigger>
            <TabsTrigger value="reportes">Reportes PDF</TabsTrigger>
          </TabsList>

          <TabsContent value="gastos" className="space-y-4">
            {/* Filtros de gastos */}
            <Card className="border-border/60">
              <CardContent className="pt-4">
                <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Funnel size={18} />
                    <span className="text-sm font-medium">Filtros:</span>
                  </div>
                  
                  <Select
                    value={expenseFilters.fase}
                    onValueChange={(value) => setExpenseFilters((prev) => ({ ...prev, fase: value === 'all' ? '' : value }))}
                  >
                    <SelectTrigger className="w-[200px]" data-testid="filter-fase-lote">
                      <SelectValue placeholder="Todas las fases" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todas las fases</SelectItem>
                      {FASES.map((fase) => (
                        <SelectItem key={fase.value} value={fase.value}>
                          {fase.label.split(' - ')[1] || fase.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  <Select
                    value={expenseFilters.categoria}
                    onValueChange={(value) => setExpenseFilters((prev) => ({ ...prev, categoria: value === 'all' ? '' : value }))}
                  >
                    <SelectTrigger className="w-[200px]" data-testid="filter-categoria-lote">
                      <SelectValue placeholder="Todas las categorías" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todas las categorías</SelectItem>
                      {CATEGORIAS.map((cat) => (
                        <SelectItem key={cat.value} value={cat.value}>
                          {cat.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  {(expenseFilters.fase || expenseFilters.categoria) && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setExpenseFilters({ fase: '', categoria: '' })}
                    >
                      Limpiar filtros
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>

            {expenses.length > 0 ? (
              <Card className="border-border/60">
                <CardHeader>
                  <CardTitle>Registro de Gastos</CardTitle>
                  <CardDescription>
                    {filteredExpenses.length === expenses.length 
                      ? `${expenses.length} gastos registrados`
                      : `Mostrando ${filteredExpenses.length} de ${expenses.length} gastos`
                    }
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {filteredExpenses.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b table-header">
                          <th className="text-left p-3">Fecha</th>
                          <th className="text-left p-3">Fase</th>
                          <th className="text-left p-3">Categoría</th>
                          <th className="text-left p-3">Descripción</th>
                          <th className="text-right p-3">Cantidad</th>
                          <th className="text-right p-3">Total</th>
                          <th className="text-center p-3">Acciones</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredExpenses.map((exp) => (
                          <tr key={exp.id} className="border-b table-row" data-testid={`expense-row-${exp.id}`}>
                            <td className="p-3">{formatDate(exp.fecha)}</td>
                            <td className="p-3">
                              <Badge variant="outline" className={`phase-badge phase-${exp.fase.replace('.', '-')}`}>
                                {getPhaseLabel(exp.fase)}
                              </Badge>
                            </td>
                            <td className="p-3">
                              <div className="flex items-center gap-2">
                                <div
                                  className="w-2 h-2 rounded-full"
                                  style={{ backgroundColor: getCategoryColor(exp.categoria) }}
                                />
                                {CATEGORIAS.find((c) => c.value === exp.categoria)?.label || exp.categoria}
                                {exp.subcategoria && (
                                  <span className="text-xs text-muted-foreground">
                                    ({exp.subcategoria})
                                  </span>
                                )}
                              </div>
                            </td>
                            <td className="p-3">{exp.descripcion}</td>
                            <td className="p-3 text-right font-mono">
                              {exp.cantidad} {exp.unidad}
                            </td>
                            <td className="p-3 text-right font-bold font-mono">
                              {exp.moneda === 'USD' ? (
                                <div className="flex flex-col items-end">
                                  <span className="text-green-600">US$ {exp.total?.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                                  <span className="text-xs text-muted-foreground">(RD$ {exp.total_dop?.toLocaleString('en-US', { minimumFractionDigits: 2 }) || '0.00'})</span>
                                </div>
                              ) : (
                                <span>RD$ {exp.total?.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                              )}
                            </td>
                            <td className="p-3 text-center">
                              <div className="flex justify-center gap-1">
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8 text-blue-600 hover:text-blue-700"
                                  onClick={() => openExpenseEditModal(exp)}
                                  data-testid={`edit-expense-${exp.id}`}
                                >
                                  <PencilSimple size={16} />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8 text-red-500 hover:text-red-700"
                                  onClick={() => openExpenseDeleteModal(exp)}
                                  data-testid={`delete-expense-${exp.id}`}
                                >
                                  <Trash size={16} />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr className="bg-muted/50 font-bold">
                          <td colSpan={5} className="p-3 text-right">
                            Total {filteredExpenses.length !== expenses.length ? '(filtrado)' : 'Inversión'}:
                          </td>
                          <td className="p-3 text-right font-mono">
                            <div className="flex flex-col items-end">
                              <span className="text-lg">RD$ {filteredExpenses.reduce((sum, e) => sum + (e.total_dop || e.total), 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                              <span className="text-sm text-green-600">US$ {filteredExpenses.reduce((sum, e) => sum + (e.total_usd || 0), 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                            </div>
                          </td>
                          <td></td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                  ) : (
                    <div className="text-center py-8 text-muted-foreground">
                      <p>No hay gastos que coincidan con los filtros seleccionados</p>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="mt-2"
                        onClick={() => setExpenseFilters({ fase: '', categoria: '' })}
                      >
                        Limpiar filtros
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            ) : (
              <Card className="border-border/60">
                <CardContent className="py-12">
                  <div className="empty-state">
                    <CurrencyDollar size={64} className="text-muted-foreground/50 mb-4" />
                    <h3 className="text-xl font-semibold mb-2">No hay gastos registrados</h3>
                    <p className="text-muted-foreground mb-6">
                      Comience registrando los gastos de este lote
                    </p>
                    <Link to={`/gastos/nuevo?lote_id=${id}`}>
                      <Button className="bg-[#E73425] hover:bg-[#D81C23]">
                        <Plus size={18} className="mr-2" />
                        Agregar Primer Gasto
                      </Button>
                    </Link>
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="graficos" className="space-y-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card className="border-border/60">
                <CardHeader>
                  <CardTitle>Gastos por Categoría</CardTitle>
                </CardHeader>
                <CardContent>
                  {pieData.length > 0 ? (
                    <>
                      <ResponsiveContainer width="100%" height={250}>
                        <PieChart>
                          <Pie
                            data={pieData}
                            cx="50%"
                            cy="50%"
                            innerRadius={50}
                            outerRadius={90}
                            paddingAngle={2}
                            dataKey="value"
                          >
                            {pieData.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={entry.color} />
                            ))}
                          </Pie>
                          <Tooltip formatter={(value) => formatCurrency(value)} />
                        </PieChart>
                      </ResponsiveContainer>
                      <div className="flex flex-wrap gap-2 mt-4 justify-center">
                        {pieData.map((item, idx) => (
                          <div key={idx} className="flex items-center gap-1.5 text-xs">
                            <div
                              className="w-3 h-3 rounded"
                              style={{ backgroundColor: item.color }}
                            />
                            <span>{item.name}</span>
                          </div>
                        ))}
                      </div>
                    </>
                  ) : (
                    <div className="h-64 flex items-center justify-center text-muted-foreground">
                      Sin datos
                    </div>
                  )}
                </CardContent>
              </Card>

              <Card className="border-border/60">
                <CardHeader>
                  <CardTitle>Gastos por Fase</CardTitle>
                </CardHeader>
                <CardContent>
                  {barData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={280}>
                      <BarChart data={barData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                        <YAxis tick={{ fontSize: 12 }} />
                        <Tooltip formatter={(value) => formatCurrency(value)} />
                        <Bar dataKey="total" fill="#E73425" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-64 flex items-center justify-center text-muted-foreground">
                      Sin datos
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="reportes" className="space-y-4">
            <Card className="border-border/60">
              <CardHeader>
                <CardTitle>Descargar Reportes PDF</CardTitle>
                <CardDescription>
                  Genere reportes detallados por categoría de gasto
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <Button
                    variant="outline"
                    className="h-auto py-4 flex flex-col items-center gap-2"
                    onClick={() => handleDownloadPDF('estado-resultados')}
                    data-testid="pdf-estado-resultados"
                  >
                    <FilePdf size={32} className="text-[#E73425]" />
                    <span className="font-semibold">Estado de Resultados</span>
                    <span className="text-xs text-muted-foreground">Resumen completo del lote</span>
                  </Button>

                  <Button
                    variant="outline"
                    className="h-auto py-4 flex flex-col items-center gap-2"
                    onClick={() => handleDownloadPDF('record-granja')}
                    data-testid="pdf-record-granja"
                  >
                    <FilePdf size={32} className="text-[#F69031]" />
                    <span className="font-semibold">Record de Granja</span>
                    <span className="text-xs text-muted-foreground">Alimento, mortalidad y descarte</span>
                  </Button>

                  <Button
                    variant="outline"
                    className="h-auto py-4 flex flex-col items-center gap-2"
                    onClick={() => handleDownloadPDF('control-alimento')}
                    data-testid="pdf-control-alimento"
                  >
                    <FilePdf size={32} className="text-[#4CAF50]" />
                    <span className="font-semibold">Control de Alimento</span>
                    <span className="text-xs text-muted-foreground">Resumen por tipo de alimento</span>
                  </Button>

                  <Button
                    variant="outline"
                    className="h-auto py-4 flex flex-col items-center gap-2"
                    onClick={() => handleDownloadPDF('ventas')}
                    data-testid="pdf-ventas"
                  >
                    <FilePdf size={32} className="text-[#9C27B0]" />
                    <span className="font-semibold">Reporte de Ventas</span>
                    <span className="text-xs text-muted-foreground">Todas las ventas del lote</span>
                  </Button>

                  {CATEGORIAS.filter((cat) => expensesByCategory[cat.value] && cat.value !== 'alimento').map((cat) => (
                    <Button
                      key={cat.value}
                      variant="outline"
                      className="h-auto py-4 flex flex-col items-center gap-2"
                      onClick={() => handleDownloadPDF('categoria', cat.value)}
                      data-testid={`pdf-${cat.value}`}
                    >
                      <div
                        className="w-8 h-8 rounded-full flex items-center justify-center"
                        style={{ backgroundColor: getCategoryColor(cat.value) + '20' }}
                      >
                        <DownloadSimple size={20} style={{ color: getCategoryColor(cat.value) }} />
                      </div>
                      <span className="font-semibold">{cat.label}</span>
                      <span className="text-xs text-muted-foreground">
                        {formatCurrency(expensesByCategory[cat.value])}
                      </span>
                    </Button>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Delete Button */}
        <div className="pt-6 border-t border-border">
          <Button
            variant="ghost"
            className="text-red-500 hover:text-red-700 hover:bg-red-50"
            onClick={() => setShowDeleteDialog(true)}
            data-testid="delete-lote-btn"
          >
            <Trash size={18} className="mr-2" />
            Eliminar Lote
          </Button>
        </div>

        {/* Close Lote Dialog */}
        <Dialog open={showCloseDialog} onOpenChange={(open) => {
          setShowCloseDialog(open);
          if (!open) setCloseSuperuserCode('');
        }}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>Cerrar Lote</DialogTitle>
              <DialogDescription>
                Los datos se han cargado automáticamente desde Ventas y Record de Granja.
              </DialogDescription>
            </DialogHeader>
            
            {loadingCloseData ? (
              <div className="py-8 text-center">
                <div className="spinner h-8 w-8 mx-auto" />
                <p className="text-sm text-muted-foreground mt-2">Cargando datos de ventas y mortalidad...</p>
              </div>
            ) : (
              <div className="space-y-4 py-4">
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                  <p className="text-xs text-emerald-600 uppercase font-medium mb-1">Datos cargados desde Ventas</p>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="ingreso_venta" className="text-sm">Ingreso Total (RD$) *</Label>
                      <div className="relative mt-1">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">RD$</span>
                        <Input
                          id="ingreso_venta"
                          type="text"
                          placeholder="0.00"
                          value={closeData.ingreso_venta ? formatNumber(parseFloat(closeData.ingreso_venta)) : ''}
                          onChange={(e) => {
                            const val = e.target.value.replace(/,/g, '');
                            if (!isNaN(val) || val === '') {
                              setCloseData({ ...closeData, ingreso_venta: val });
                            }
                          }}
                          data-testid="close-ingreso"
                          className="pl-12 text-right font-mono"
                        />
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="cantidad_vendida" className="text-sm">Pollos Vendidos</Label>
                      <Input
                        id="cantidad_vendida"
                        type="text"
                        placeholder={formatNumber(lote.cantidad_pollitos)}
                        value={closeData.cantidad_vendida ? formatNumber(parseInt(closeData.cantidad_vendida)) : ''}
                        onChange={(e) => {
                          const val = e.target.value.replace(/,/g, '');
                          if (!isNaN(val) || val === '') {
                            setCloseData({ ...closeData, cantidad_vendida: val });
                          }
                        }}
                        data-testid="close-cantidad"
                        className="mt-1 text-right font-mono"
                      />
                    </div>
                  </div>
                </div>
                
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <p className="text-xs text-red-600 uppercase font-medium mb-1">Datos cargados desde Record de Granja</p>
                  <div>
                    <Label htmlFor="mortalidad" className="text-sm">Mortalidad + Descarte Total</Label>
                    <Input
                      id="mortalidad"
                      type="text"
                      placeholder="0"
                      value={closeData.mortalidad ? formatNumber(parseInt(closeData.mortalidad)) : '0'}
                      onChange={(e) => {
                        const val = e.target.value.replace(/,/g, '');
                        if (!isNaN(val) || val === '') {
                          setCloseData({ ...closeData, mortalidad: val || '0' });
                        }
                      }}
                      data-testid="close-mortalidad"
                      className="mt-1 text-right font-mono"
                    />
                  </div>
                </div>
                
                <div className="bg-muted/50 rounded-lg p-4">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-muted-foreground">Pollitos Inicial:</p>
                      <p className="font-semibold">{formatNumber(lote.cantidad_pollitos)}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Inversión Total:</p>
                      <p className="font-semibold text-red-600">{formatCurrency(lote.total_inversion)}</p>
                    </div>
                  </div>
                </div>
                
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 text-amber-700 mb-2">
                    <ShieldCheck size={18} />
                    <span className="text-xs uppercase font-medium">Acción Protegida</span>
                  </div>
                  <div>
                    <Label htmlFor="close-superuser-code" className="text-sm">Código de Super Usuario *</Label>
                    <Input
                      id="close-superuser-code"
                      type="password"
                      placeholder="Ingrese el código..."
                      value={closeSuperuserCode}
                      onChange={(e) => setCloseSuperuserCode(e.target.value)}
                      data-testid="close-superuser-code"
                      className="mt-1"
                    />
                  </div>
                </div>
                
                <p className="text-xs text-muted-foreground">
                  * Puede modificar los valores si necesita ajustarlos antes de cerrar.
                </p>
              </div>
            )}
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCloseDialog(false)}>
                Cancelar
              </Button>
              <Button
                className="bg-[#E73425] hover:bg-[#D81C23]"
                onClick={handleCloseLote}
                disabled={actionLoading || loadingCloseData}
                data-testid="confirm-close-btn"
              >
                {actionLoading ? <div className="spinner h-5 w-5" /> : 'Cerrar Lote'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Dialog */}
        <Dialog open={showDeleteDialog} onOpenChange={(open) => {
          setShowDeleteDialog(open);
          if (!open) setDeleteSuperuserCode('');
        }}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-600">
                <Warning size={24} />
                Eliminar Lote
              </DialogTitle>
              <DialogDescription>
                Esta acción no se puede deshacer. Se eliminarán todos los gastos asociados.
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-2">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-sm text-red-600 mb-2">
                  Está a punto de eliminar el lote:
                </p>
                <p className="font-semibold text-red-700">{lote?.nombre}</p>
                <p className="text-sm text-red-600 mt-1">
                  Inversión: {formatCurrency(lote?.total_inversion || 0)}
                </p>
              </div>
              
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <div className="flex items-center gap-2 text-amber-700 text-sm">
                  <ShieldCheck size={18} />
                  <span>Acción protegida - Requiere código de super usuario</span>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="delete-lote-code">Código de Super Usuario</Label>
                <Input
                  id="delete-lote-code"
                  type="password"
                  value={deleteSuperuserCode}
                  onChange={(e) => setDeleteSuperuserCode(e.target.value)}
                  placeholder="Ingrese el código..."
                  data-testid="delete-lote-code"
                />
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
                Cancelar
              </Button>
              <Button
                variant="destructive"
                onClick={handleDeleteLote}
                disabled={actionLoading || !deleteSuperuserCode}
                data-testid="confirm-delete-btn"
              >
                {actionLoading ? <div className="spinner h-5 w-5" /> : (
                  <>
                    <Trash size={16} className="mr-2" />
                    Eliminar Lote
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Modal de Eliminar Gasto (con Super Usuario) */}
        <Dialog open={showExpenseDeleteModal} onOpenChange={setShowExpenseDeleteModal}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-600">
                <Warning size={24} />
                Eliminar Gasto
              </DialogTitle>
              <DialogDescription>
                Esta acción no se puede deshacer
              </DialogDescription>
            </DialogHeader>
            
            {expenseToDelete && (
              <div className="space-y-4">
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <p className="text-sm text-red-600 mb-2">
                    Está a punto de eliminar el gasto:
                  </p>
                  <p className="font-semibold text-red-700">{expenseToDelete.descripcion}</p>
                  <p className="text-sm text-red-600 mt-1">
                    Monto: {formatCurrency(expenseToDelete.total)}
                  </p>
                </div>
                
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <div className="flex items-center gap-2 text-amber-700 text-sm">
                    <ShieldCheck size={18} />
                    <span>Acción protegida - Requiere código de super usuario</span>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="delete-expense-code">Código de Super Usuario</Label>
                  <Input
                    id="delete-expense-code"
                    type="password"
                    value={expenseDeleteCode}
                    onChange={(e) => setExpenseDeleteCode(e.target.value)}
                    placeholder="Ingrese el código..."
                    data-testid="delete-expense-code"
                  />
                </div>
              </div>
            )}
            
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => setShowExpenseDeleteModal(false)}>
                Cancelar
              </Button>
              <Button
                variant="destructive"
                onClick={handleDeleteExpense}
                disabled={deletingExpense || !expenseDeleteCode}
              >
                {deletingExpense ? (
                  <div className="spinner h-4 w-4" />
                ) : (
                  <>
                    <Trash size={16} className="mr-2" />
                    Eliminar Gasto
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Modal de Editar Gasto (con Super Usuario) */}
        <Dialog open={showExpenseEditModal} onOpenChange={setShowExpenseEditModal}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-blue-600">
                <PencilSimple size={24} />
                Editar Gasto
              </DialogTitle>
              <DialogDescription>
                Ingrese el código de super usuario para editar
              </DialogDescription>
            </DialogHeader>
            
            {expenseToEdit && (
              <div className="space-y-4">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="text-sm text-blue-600 mb-2">
                    Editar el gasto:
                  </p>
                  <p className="font-semibold text-blue-700">{expenseToEdit.descripcion}</p>
                  <p className="text-sm text-blue-600 mt-1">
                    Monto: {formatCurrency(expenseToEdit.total)}
                  </p>
                </div>
                
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <div className="flex items-center gap-2 text-amber-700 text-sm">
                    <ShieldCheck size={18} />
                    <span>Acción protegida - Requiere código de super usuario</span>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="edit-expense-code">Código de Super Usuario</Label>
                  <Input
                    id="edit-expense-code"
                    type="password"
                    value={expenseEditCode}
                    onChange={(e) => setExpenseEditCode(e.target.value)}
                    placeholder="Ingrese el código..."
                    data-testid="edit-expense-code"
                  />
                </div>
              </div>
            )}
            
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => setShowExpenseEditModal(false)}>
                Cancelar
              </Button>
              <Button
                className="bg-blue-600 hover:bg-blue-700"
                onClick={handleValidateExpenseEdit}
                disabled={validatingExpenseEdit || !expenseEditCode}
              >
                {validatingExpenseEdit ? (
                  <div className="spinner h-4 w-4" />
                ) : (
                  <>
                    <ShieldCheck size={16} className="mr-2" />
                    Validar y Editar
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
